import { FeatureFlag } from '@ps/web-core-enums';
import { useFeature } from '@ps/web-core-store';
import { CashierPath } from '@ps/web-pam-types';
import { useCallback, useEffect } from 'react';
import { useSelector } from 'react-redux';

import useFromEvent from '@swf/core/hooks/useFromEvent';
import useWizardContext from '@swf/core/hooks/useWizardContext';
import useWizardExit from '@swf/core/hooks/useWizardExit';
import useWizardJump from '@swf/core/hooks/useWizardJump';
import { select } from '@swf/core/store';
import type { RmfTriggerPoint } from '@swf/core/types/Window';

import useRmfServices from '../components/RMF/hooks/useRmfServices';
import { getRmfRequiredPath } from '../utils/cashier';

type WizardJump = (target: string, triggerPoint: RmfTriggerPoint) => void;

const useTriggerRMFFlow = (): WizardJump => {
  const { jumpForward, jumpBack } = useWizardJump();
  const { rmfUpdateFormData } = useRmfServices();
  const { rmfRealMoneyInformationCheck } = useRmfServices();
  const { data } = useWizardContext();
  const isMobileEmbedded = useSelector(select.device.isMobileEmbedded);
  const site = useSelector(select.cms.getSiteId);
  const country = useSelector(select.account.getCountry);
  const isUSPAEnhancedFlow = useFeature(FeatureFlag.SwcUSPAEnhancedFlow);
  const isSignUpOnePageEnabled = useFeature(FeatureFlag.SwcSignupOnePage);
  const path = getRmfRequiredPath(site, isSignUpOnePageEnabled, isUSPAEnhancedFlow, country);
  const fromEvent = useFromEvent();
  const wizardContext = useWizardContext();
  const exitWizard = useWizardExit();

  const handleMessage = useCallback(
    ({ detail }: CustomEvent) => {
      const { message } = detail.payload;

      if (data.rmfTargetToJump && message === 'rmfCompleted') {
        jumpForward(data.rmfTargetToJump);
      }
    },
    [data.rmfTargetToJump, jumpForward]
  );

  const handleClose = useCallback(async () => {
    const { realMoneyInformation } = await rmfRealMoneyInformationCheck();
    if (!realMoneyInformation && data.rmfTargetToJump) {
      return jumpForward(data.rmfTargetToJump);
    } else {
      exitWizard();
    }
  }, [data.rmfTargetToJump, exitWizard, jumpForward, rmfRealMoneyInformationCheck]);

  useEffect(() => {
    if (isMobileEmbedded) {
      fromEvent(document, 'starsbroadcast').subscribe((e) => handleMessage(e as CustomEvent));
      fromEvent(document, 'starsresume').subscribe(() => handleClose());
    }
  }, [fromEvent, handleMessage, isMobileEmbedded, jumpForward, handleClose]);

  return useCallback(
    async (target: string, triggerPoint: RmfTriggerPoint) => {
      data.rmfTargetToJump = target;
      await rmfUpdateFormData(wizardContext);
      if (window.location.pathname === CashierPath.Signup) {
        return jumpBack(path);
      } else {
        return jumpForward(path);
      }
    },
    [data, jumpBack, jumpForward, path, rmfUpdateFormData, wizardContext]
  );
};

export default useTriggerRMFFlow;
